/** Egypt Guide ornaments: inline SVG, decorative, scaled to their container. */

/** The ankh, hieroglyph for life: the guide's distinction mark. */
export function Ankh({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size * 0.7}
      height={size}
      viewBox="0 0 14 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={className}
    >
      <ellipse cx="7" cy="5" rx="3.6" ry="4" />
      <path d="M7 9v10M2 10.5h10" />
    </svg>
  );
}

/** One to three ankhs, or a four-point star for a Selected venue. */
export function DistinctionMark({ count, size = 18 }: { count: number; size?: number }) {
  if (count <= 0) {
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" style={{ color: "var(--eg-gold)" }}>
        <path d="M10 1 L12 8 L19 10 L12 12 L10 19 L8 12 L1 10 L8 8 Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 3, color: "var(--eg-gold)" }} aria-hidden="true">
      {Array.from({ length: Math.min(count, 3) }, (_, i) => (
        <Ankh key={i} size={size} />
      ))}
    </span>
  );
}

/** A 3D cube, for "step inside in 3D". */
export function CubeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
      <path d="M12 3 L20 7.5 V16.5 L12 21 L4 16.5 V7.5 Z" />
      <path d="M12 12 L20 7.5 M12 12 L4 7.5 M12 12 V21" />
    </svg>
  );
}

/**
 * Gold five-point stars on lapis, after the night skies painted on tomb and
 * temple ceilings. `id` keeps two bands on one page from sharing a pattern.
 */
export function StarBand({ id, height = 34 }: { id: string; height?: number }) {
  return (
    <div aria-hidden="true" style={{ background: "var(--eg-lapis)" }}>
      <svg width="100%" height={height} style={{ display: "block" }}>
        <defs>
          <pattern id={id} width="44" height={height} patternUnits="userSpaceOnUse">
            <path
              d={`M22 ${height / 2 - 8} l1.8 5.6 h5.8 l-4.7 3.4 1.8 5.6 -4.7 -3.4 -4.7 3.4 1.8 -5.6 -4.7 -3.4 h5.8 Z`}
              fill="#d4af37"
              opacity="0.9"
            />
            <circle cx="4" cy={height - 6} r="1.2" fill="#d4af37" opacity="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height={height} fill={`url(#${id})`} />
      </svg>
    </div>
  );
}

/** Upright reeds in gold, lapis and sand: the cornice over a temple doorway. */
export function ReedBand({ id, height = 14 }: { id: string; height?: number }) {
  return (
    <svg width="100%" height={height} aria-hidden="true" style={{ display: "block" }}>
      <defs>
        <pattern id={id} width="12" height={height} patternUnits="userSpaceOnUse">
          <rect x="0" width="4" height={height} fill="#b8893b" />
          <rect x="4" width="4" height={height} fill="#13294b" />
          <rect x="8" width="4" height={height} fill="#c9bca4" />
        </pattern>
      </defs>
      <rect width="100%" height={height} fill={`url(#${id})`} />
    </svg>
  );
}

/** A band of lotus buds between rules, after temple-wall friezes. */
export function LotusFrieze({ id }: { id: string }) {
  return (
    <svg width="100%" height="28" aria-hidden="true" style={{ display: "block" }}>
      <defs>
        <pattern id={id} width="56" height="28" patternUnits="userSpaceOnUse">
          <path d="M0 14 H56" stroke="#16120e" strokeWidth="1" />
          <path d="M28 4 C22 10 22 18 28 24 C34 18 34 10 28 4 Z" fill="#f5f0e6" stroke="#8a6420" strokeWidth="1.2" />
          <path d="M28 8 L28 20" stroke="#8a6420" strokeWidth="1.2" />
          <rect x="4" y="10" width="8" height="8" transform="rotate(45 8 14)" fill="#16120e" />
        </pattern>
      </defs>
      <rect width="100%" height="28" fill={`url(#${id})`} />
    </svg>
  );
}
